<template>
  <v-card>
    <v-card-title :class="contractMode.details.color">
      <v-row justify="space-between" dense>
        <v-col cols="auto" :style="{maxWidth: `calc(100% - ${$vuetify.breakpoint.lgAndUp ? 88 : 48}px)`}">
          <span class="headline white--text mr-2" >
            {{ dialogTitle }}
          </span>
        </v-col>
        <v-col cols="auto">
          <BaseDialogActions hideRefresh @close="$emit('close')"/>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text>
      <RateVerification
      :items="aggregatedData"
      :groupingKeys="groupingKeys"
      :expandToContract="expandToContract"
      @refresh-batch="$emit('refresh-batch')">
        <template #title>
          <span v-if="isSBC && singleContract" class="title">
            <span v-for="(ci, i) in contractIcons" :key="i">
              <Icon
              :icon="ci.icon"
              :tooltipText="$t(ci.tooltip)"
              iconColor="primary"/>
              <span class="mr-1 mt-n1 body-1">{{ ci.value(sourceData[0].contractInfo) }}</span>
            </span>
          </span>
          <span v-else-if="isSBC" class="title">
            {{ $t('allContracts') }}
          </span>
          <span v-else-if="singlePayee" class="title">
            {{ $t('counterpartyColon', { counterparty: sourceData[0].payeeName }) }}
          </span>
          <span v-else class="title">
            {{ $t('allPayees') }}
          </span>
        </template>
      </RateVerification>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import { rateVerificationGroupingKeys } from '@/headers/Cycles.js'
import { contractIconsFor } from '../accounts-payable-module/ContractIcons'
import { aggregateSettlementsByContract, aggregateSettlementsByPayee } from './rateVerificationAggregator.js'

export default {
  name: 'RateVerificationDialog',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    RateVerification: () => import('./RateVerification.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    payeeId: {
      type: Number,
      required: false,
      default: undefined
    },
    contractId: {
      type: Number,
      required: false,
      default: undefined
    },
    contractMode: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    sourceData: []
  }),

  watch: {
    currentSubModule () {
      this.refreshData()
    }
  },

  computed: {
    ...mapGetters('settlements', ['currentSubModuleKey', 'currentSubModule', 'dateConfiguration']),

    dialogTitle () {
      return this.$t('rateVerificationSubtitle', { exportBatch: this.dateConfiguration.exportBatch.label, subModule: this.$t(this.currentSubModuleKey) })
    },

    isSBC () {
      return this.currentSubModuleKey === 'settlementsByContract'
    },

    singleContract () {
      return this.isSBC && !!this.contractId
    },

    singlePayee () {
      return !this.isSBC && !!this.payeeId
    },

    expandToContract () {
      return this.isSBC ? !this.singleContract : true
    },

    groupingKeys () {
      const includePayee = this.isSBC || !this.singlePayee
      return rateVerificationGroupingKeys(includePayee)
    },

    contractIcons () {
      if (!this.isSBC || !this.singleContract || this.sourceData.length === 0) return []
      return contractIconsFor(this.sourceData[0].type, 'contract')
    },

    aggregatedData () {
      if (this.sourceData.length === 0) return []
      return this.isSBC
        ? aggregateSettlementsByContract(this.sourceData)
        : aggregateSettlementsByPayee(this.sourceData)
    }
  },

  created () {
    this.refreshData()
  },

  methods: {
    refreshData () {
      if (this.isSBC) {
        this.sourceData = this.contractId
          ? [this.currentSubModule.settlements.find(s => s.contractInfo.contractId === this.contractId)]
          : this.currentSubModule.settlements
      } else {
        this.sourceData = this.payeeId
          ? [this.currentSubModule.payees.find(p => p.payeeId === this.payeeId)]
          : this.currentSubModule.payees
      }
    }
  }
}
</script>
